import React from 'react';
import { Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import logoWhite from '../assets/coachmeupp-logo-white.png';
import { useTranslation } from 'react-i18next';

const Footer = ({ onBlogClick, onCaseStudiesClick, onResourcesClick, onCareersClick, onPrivacyClick, onTermsClick, onCoachesClick, onAnalyticsClick, onEmailTemplatesClick, onPartnersAdminClick, onServicesAdminClick, onSubscriptionsAdminClick, onShopClick, onABTestAdminClick, onAboutClick, onDiscountCodesAdminClick }) => {
  const { t } = useTranslation();

  const handleLinkClick = (e, url) => {
    if (url === '/blog') {
      e.preventDefault();
      onBlogClick && onBlogClick();
    } else if (url === '/case-studies') {
      e.preventDefault();
      onCaseStudiesClick && onCaseStudiesClick();
    } else if (url === '/partners-admin') {
      e.preventDefault();
      onPartnersAdminClick && onPartnersAdminClick();
    } else if (url === '/services-admin') {
      e.preventDefault();
      onServicesAdminClick && onServicesAdminClick();
    } else if (url === '/subscriptions-admin') {
      e.preventDefault();
      onSubscriptionsAdminClick && onSubscriptionsAdminClick();
    } else if (url === '/ab-test-admin') {
      e.preventDefault();
      onABTestAdminClick && onABTestAdminClick();
    } else if (url === '/discount-codes-admin') {
      e.preventDefault();
      onDiscountCodesAdminClick && onDiscountCodesAdminClick();
    } else if (url === '/shop') {
      e.preventDefault();
      onShopClick && onShopClick();
    } else if (url === '/resources') {
      e.preventDefault();
      onResourcesClick && onResourcesClick();
    } else if (url === '/careers') {
      e.preventDefault();
      onCareersClick && onCareersClick();
    } else if (url === '/privacy') {
      e.preventDefault();
      onPrivacyClick && onPrivacyClick();
    } else if (url === '/terms') {
      e.preventDefault();
      onTermsClick && onTermsClick();
    } else if (url === '/about') {
      e.preventDefault();
      onAboutClick && onAboutClick();
    } else if (url === '/analytics') {
      e.preventDefault();
      onAnalyticsClick && onAnalyticsClick();
    } else if (url === '/email-templates') {
      e.preventDefault();
      onEmailTemplatesClick && onEmailTemplatesClick();
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <div className="mb-6">
              <img 
                src={logoWhite}
                alt="CoachMeUpp Logo"
                className="h-28 w-auto"
              />
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-blue-400" />
                <a href="mailto:bilgi@coachmeupp.com" className="hover:text-white transition-colors">
                  {t('footer.contact.email')}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-green-400" />
                <a href="tel:+905337635388" className="hover:text-white transition-colors">
                  {t('footer.contact.phone')}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-red-400" />
                <span>{t('footer.contact.location')}</span>
              </div>
            </div>
            
            {/* Social Links - LinkedIn only */}
            <div className="flex gap-4">
              <a
                href="https://linkedin.com/company/coachmeupp"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-blue-600 transition-all duration-300 hover:scale-110"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          {/* Platform + Company Column */}
          <div className="space-y-8">
            {/* Platform */}
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.sections.platform.title')}</h3>
              <ul className="space-y-3">
                <li>
                  <a href="#process" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.platform.links.howItWorks')}
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onCoachesClick && onCoachesClick(); }}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.platform.links.ourCoaches')}
                  </a>
                </li>
                <li>
                  <a href="#testimonials" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.platform.links.successStories')}
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.sections.company.title')}</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="/about"
                    onClick={(e) => handleLinkClick(e, '/about')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.aboutUs', 'About Us')}
                  </a>
                </li>
                <li>
                  <a
                    href="/careers"
                    onClick={(e) => handleLinkClick(e, '/careers')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.careers')}
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.company.links.contact')}
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    onClick={(e) => handleLinkClick(e, '/privacy')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.privacy')}
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    onClick={(e) => handleLinkClick(e, '/terms')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.terms')}
                  </a>
                </li>
                <li>
                  <a
                    href="/analytics"
                    onClick={(e) => handleLinkClick(e, '/analytics')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.analytics', 'Analytics')}
                  </a>
                </li>
                <li>
                  <a
                    href="/email-templates"
                    onClick={(e) => handleLinkClick(e, '/email-templates')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.emailTemplates', 'Email Templates')}
                  </a>
                </li>
                <li>
                  <a
                    href="/partners-admin"
                    onClick={(e) => handleLinkClick(e, '/partners-admin')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.partners', 'Partner Logos')}
                  </a>
                </li>
                <li>
                  <a
                    href="/services-admin"
                    onClick={(e) => handleLinkClick(e, '/services-admin')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.servicesAdmin', 'Services Admin')}
                  </a>
                </li>
                <li>
                  <a
                    href="/subscriptions-admin"
                    onClick={(e) => handleLinkClick(e, '/subscriptions-admin')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.subscriptionsAdmin', 'Subscriptions')}
                  </a>
                </li>
                <li>
                  <a
                    href="/ab-test-admin"
                    onClick={(e) => handleLinkClick(e, '/ab-test-admin')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.company.links.abTestAdmin', 'A/B Testing')}
                  </a>
                </li>
                <li>
                  <a
                    href="/discount-codes-admin"
                    onClick={(e) => handleLinkClick(e, '/discount-codes-admin')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                    data-testid="footer-discount-codes-admin-link"
                  >
                    {t('footer.sections.company.links.discountCodesAdmin', 'Discount Codes')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Solutions + Resources Column */}
          <div className="space-y-8">
            {/* Solutions */}
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.sections.solutions.title')}</h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="/shop" 
                    onClick={(e) => handleLinkClick(e, '/shop')}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.solutions.links.shop', 'Shop Services')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.solutions.links.executive')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.solutions.links.leadership')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.solutions.links.team')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300">
                    {t('footer.sections.solutions.links.highPotential')}
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Resources */}
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.sections.resources.title')}</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onBlogClick && onBlogClick(); }}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.resources.links.blog')}
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onCaseStudiesClick && onCaseStudiesClick(); }}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.resources.links.caseStudies')}
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onResourcesClick && onResourcesClick(); }}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {t('footer.sections.resources.links.whitepapers')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Secure Payments Strip */}
      <div className="border-t border-gray-800 bg-gray-950/40">
        <div className="container mx-auto px-6 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
            <span className="text-xs uppercase tracking-wider text-gray-400">
              {t('footer.securePaymentsBy', 'Secure payments by')}
            </span>
            <div className="bg-white rounded-md px-4 py-2 shadow-sm">
              <img
                src="/paytr-badge.png"
                alt="PayTR Güvenli Öde – Visa, Mastercard, Troy"
                className="h-8 w-auto"
                loading="lazy"
                data-testid="paytr-logo-footer"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              {t('footer.copyright')}
            </p>
            <div className="flex gap-6 text-sm">
              <a 
                href="/privacy" 
                onClick={(e) => handleLinkClick(e, '/privacy')}
                className="text-gray-500 hover:text-white transition-colors"
              >
                {t('footer.sections.company.links.privacy')}
              </a>
              <a 
                href="/terms"
                onClick={(e) => handleLinkClick(e, '/terms')}
                className="text-gray-500 hover:text-white transition-colors"
              >
                {t('footer.sections.company.links.terms')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
